// components/RecordingStudio/MusicTrimModal.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  X,
  Scissors,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Clock,
} from "lucide-react";

const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

const StatCard = ({ icon, label, value, color, bgColor }) => (
  <div
    className="rounded-2xl p-4 flex flex-col items-center gap-2"
    style={{ background: bgColor, border: "1px solid rgba(255,255,255,0.06)" }}
  >
    <div style={{ color }}>{icon}</div>
    <span
      className="text-[10px] font-bold uppercase tracking-widest"
      style={{ color: "rgba(255,255,255,0.3)" }}
    >
      {label}
    </span>
    <span
      className="text-xl font-bold font-mono tabular-nums"
      style={{ color }}
    >
      {value}
    </span>
  </div>
);

const Handle = ({ pct, side, onMouseDown, label, color }) => (
  <div
    style={{
      position: "absolute",
      left: `${pct}%`,
      top: 0,
      bottom: 0,
      width: 0,
      zIndex: 20,
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: -1,
        width: 2,
        background: color,
      }}
    />
    <div
      onMouseDown={onMouseDown}
      onTouchStart={onMouseDown}
      style={{
        position: "absolute",
        top: "50%",
        left: -10,
        transform: "translateY(-50%)",
        cursor: "ew-resize",
        touchAction: "none",
        width: 20,
        height: 44,
        borderRadius: 8,
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 4px 16px ${color}60`,
      }}
    >
      <div className="flex flex-col gap-[3px]">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 3,
              height: 3,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.4)",
            }}
          />
        ))}
      </div>
    </div>
    {/* Time label */}
    <div
      style={{
        position: "absolute",
        bottom: "calc(100% + 8px)",
        left: "50%",
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
        pointerEvents: "none",
        fontSize: 10,
        fontFamily: "monospace",
        fontWeight: 700,
        color,
        background: "rgba(0,0,0,0.8)",
        padding: "2px 8px",
        borderRadius: 6,
        border: `1px solid ${color}50`,
      }}
    >
      {label}
    </div>
  </div>
);

const MusicTrimModal = ({
  track,
  initialTrimStart,
  initialTrimEnd,
  onApply,
  onClose,
}) => {
  const [totalDur, setTotalDur] = useState(track.duration || 0);
  const [trimStart, setTrimStart] = useState(initialTrimStart ?? 0);
  const [trimEnd, setTrimEnd] = useState(
    initialTrimEnd != null && initialTrimEnd > 0
      ? initialTrimEnd
      : track.duration || 0,
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);

  const barRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const dragging = useRef(null);

  const finalDur = Math.max(0, trimEnd - trimStart);
  const trimmedAmt = totalDur - finalDur;
  const retention = totalDur > 0 ? (finalDur / totalDur) * 100 : 100;

  useEffect(() => {
    const audio = new Audio(track.audioUrl);
    audio.volume = 0.7;
    audioRef.current = audio;
    // If duration wasn't stored, read it from the audio element itself
    audio.addEventListener("loadedmetadata", () => {
      const dur = Math.round(audio.duration);
      if (dur && dur > 0 && totalDur === 0) {
        setTotalDur(dur);
        setTrimEnd((prev) => (prev === 0 ? dur : prev));
      }
    });
    return () => {
      audio.pause();
      cancelAnimationFrame(rafRef.current);
    };
  }, [track.audioUrl]);

  const startPreview = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = trimStart;
    audio.play().catch(() => {});
    setIsPlaying(true);
    const tick = () => {
      if (!audioRef.current) return;
      const t = audioRef.current.currentTime;
      setPreviewTime(t);
      if (t >= trimEnd) {
        audioRef.current.pause();
        setIsPlaying(false);
        setPreviewTime(trimStart);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [trimStart, trimEnd]);

  const stopPreview = useCallback(() => {
    audioRef.current?.pause();
    cancelAnimationFrame(rafRef.current);
    setIsPlaying(false);
    setPreviewTime(trimStart);
  }, [trimStart]);

  const togglePreview = () => {
    if (isPlaying) stopPreview();
    else startPreview();
  };

  useEffect(() => {
    if (isPlaying) stopPreview();
  }, [trimStart, trimEnd]);

  const getPosFromEvent = useCallback(
    (e) => {
      const bar = barRef.current;
      if (!bar) return 0;
      const rect = bar.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - rect.left) / rect.width),
      );
      return parseFloat((ratio * totalDur).toFixed(2));
    },
    [totalDur],
  );

  const onMouseDown = useCallback(
    (handle) => (e) => {
      e.preventDefault();
      dragging.current = handle;
    },
    [],
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const pos = getPosFromEvent(e);
      const snapped = Math.round(pos / 0.25) * 0.25;
      if (dragging.current === "start")
        setTrimStart(Math.min(snapped, trimEnd - 0.5));
      else setTrimEnd(Math.max(snapped, trimStart + 0.5));
    };
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [getPosFromEvent, trimStart, trimEnd]);

  const startPct = totalDur > 0 ? (trimStart / totalDur) * 100 : 0;
  const endPct = totalDur > 0 ? (trimEnd / totalDur) * 100 : 100;
  const previewPct = totalDur > 0 ? (previewTime / totalDur) * 100 : 0;
  const retentionColor =
    retention < 50 ? "#f87171" : retention < 80 ? "#fbbf24" : "#34d399";

  const waveHeights = [
    3, 7, 5, 9, 4, 8, 6, 10, 3, 7, 5, 9, 6, 4, 8, 3, 9, 5, 7, 4, 10, 6, 3, 8, 5,
    7, 4, 6, 8, 5, 9, 4, 7, 5, 9, 4, 8, 3, 7, 5, 10, 6, 4, 9, 3, 8, 5, 7, 6, 9,
    4, 8, 3, 7, 5, 10,
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
    >
      <div
        className="w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: "linear-gradient(180deg, #0f1117 0%, #0a0c12 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: "0 40px 80px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #d97706, #b45309)",
              }}
            >
              <Scissors size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-tight">
                Trim Music
              </h2>
              <p className="text-xs text-gray-600">
                Drag handles to set loop region
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-600 hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Track info */}
          <div
            className="flex items-center gap-4 p-3.5 rounded-2xl"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              {track.thumbnailUrl ? (
                <img
                  src={track.thumbnailUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span className="text-amber-500 text-lg">♪</span>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{track.title}</p>
              <p className="text-xs text-gray-600 mt-0.5">{track.artist}</p>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-3">
            <StatCard
              icon={<Clock size={16} />}
              label="Original"
              value={fmt(totalDur)}
              color="#60a5fa"
              bgColor="rgba(59,130,246,0.07)"
            />
            <StatCard
              icon={<Scissors size={16} />}
              label="Removed"
              value={fmt(trimmedAmt)}
              color="#fbbf24"
              bgColor="rgba(245,158,11,0.07)"
            />
            <StatCard
              icon={<Play size={16} />}
              label="Final"
              value={fmt(finalDur)}
              color="#34d399"
              bgColor="rgba(52,211,153,0.07)"
            />
          </div>

          {/* Waveform + handles */}
          <div>
            <div
              ref={barRef}
              className="relative overflow-visible"
              style={{ height: 72 }}
            >
              {/* Outer shadow regions */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${startPct}%`,
                  background: "rgba(0,0,0,0.5)",
                  zIndex: 5,
                  borderRadius: "8px 0 0 8px",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: `${endPct}%`,
                  top: 0,
                  bottom: 0,
                  right: 0,
                  background: "rgba(0,0,0,0.5)",
                  zIndex: 5,
                  borderRadius: "0 8px 8px 0",
                }}
              />

              {/* Full track waveform */}
              <div
                className="absolute inset-0 flex items-center justify-center gap-[2px] px-2 rounded-2xl overflow-hidden"
                style={{
                  background: "rgba(217,119,6,0.1)",
                  border: "1px solid rgba(217,119,6,0.2)",
                }}
              >
                {waveHeights.map((h, i) => (
                  <div
                    key={i}
                    style={{
                      width: 2,
                      height: `${h * 3}px`,
                      background: "#f59e0b",
                      borderRadius: 9999,
                      opacity: 0.6,
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>

              {/* Active region highlight */}
              <div
                style={{
                  position: "absolute",
                  left: `${startPct}%`,
                  width: `${endPct - startPct}%`,
                  top: 0,
                  bottom: 0,
                  zIndex: 4,
                  background: "rgba(251,191,36,0.15)",
                  border: "1px solid rgba(251,191,36,0.3)",
                }}
              />

              {/* Playhead */}
              {isPlaying && (
                <div
                  style={{
                    position: "absolute",
                    left: `${previewPct}%`,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: "rgba(255,255,255,0.9)",
                    zIndex: 10,
                  }}
                />
              )}

              <Handle
                pct={startPct}
                side="left"
                onMouseDown={onMouseDown("start")}
                label={fmt(trimStart)}
                color="#60a5fa"
              />
              <Handle
                pct={endPct}
                side="right"
                onMouseDown={onMouseDown("end")}
                label={fmt(trimEnd)}
                color="#34d399"
              />
            </div>

            {/* Time labels */}
            <div className="flex justify-between mt-2 px-1">
              <span className="text-[10px] text-gray-700 font-mono">0:00</span>
              <span className="text-[10px] text-gray-700 font-mono">
                {fmt(totalDur / 2)}
              </span>
              <span className="text-[10px] text-gray-700 font-mono">
                {fmt(totalDur)}
              </span>
            </div>
          </div>

          {/* Retention bar */}
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-xs text-gray-600">Kept</span>
              <span
                className="text-xs font-bold font-mono tabular-nums"
                style={{ color: retentionColor }}
              >
                {retention.toFixed(0)}%
              </span>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.06)" }}
            >
              <div
                style={{
                  width: `${retention}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${retentionColor}80, ${retentionColor})`,
                  transition: "width 0.15s ease",
                  borderRadius: 9999,
                }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={togglePreview}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                background: isPlaying
                  ? "rgba(217,119,6,0.2)"
                  : "rgba(255,255,255,0.05)",
                border: isPlaying
                  ? "1px solid rgba(217,119,6,0.4)"
                  : "1px solid rgba(255,255,255,0.08)",
                color: isPlaying ? "#fbbf24" : "#9ca3af",
              }}
            >
              {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              {isPlaying ? "Stop" : "Preview"}
            </button>

            <div
              style={{
                width: 1,
                height: 20,
                background: "rgba(255,255,255,0.08)",
              }}
            />

            {[15, 30, 60].map((sec) => (
              <button
                key={sec}
                onClick={() => {
                  setTrimStart(0);
                  setTrimEnd(Math.min(sec, totalDur));
                }}
                disabled={totalDur < sec}
                className="px-3 py-2 text-xs rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  color: "#6b7280",
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = "#6b7280";
                }}
              >
                {sec}s
              </button>
            ))}
            <button
              onClick={() => {
                setTrimStart(0);
                setTrimEnd(totalDur);
              }}
              className="px-3 py-2 text-xs rounded-xl transition-all"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
                color: "#6b7280",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#6b7280";
              }}
            >
              Full
            </button>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 pb-6"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            paddingTop: "1.25rem",
          }}
        >
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:text-white transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onApply({ trimStart, trimEnd, duration: finalDur })}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
            style={{
              background: "linear-gradient(135deg, #d97706, #b45309)",
              color: "white",
              boxShadow: "0 4px 16px rgba(217,119,6,0.3)",
            }}
          >
            <Scissors size={14} />
            Apply Trim
          </button>
        </div>
      </div>
    </div>
  );
};

export default MusicTrimModal;
